import React, { useState, useEffect } from "react";
import { Truck, ArrowRight, ShieldCheck, CloudSun, Gavel, FileText, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { COMPANY_LOGO, COMPANY_NAME, COMPANY_FULL, PARENT_COMPANY, PRODUCT_NAME } from "../lib/brand";

const HERO = "https://images.unsplash.com/photo-1562954203-74c7fb884cba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxmcmVpZ2h0JTIwdHJ1Y2slMjBoaWdod2F5JTIwc3Vuc2V0fGVufDB8fHx8MTc4MjA3MzE3N3ww&ixlib=rb-4.1.0&q=85";

const AUDIENCES = [
  { key: "driver", label: "Drivers" },
  { key: "broker", label: "Brokers & Carriers" },
  { key: "shipper", label: "Shippers" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState({});
  const [audience, setAudience] = useState("broker");
  useEffect(() => { api.get("/billing/plans").then(r => setPlans(r.data)).catch(() => {}); }, []);
  const planKeys = Object.keys(plans).filter((k) => plans[k].audience === audience);
  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="absolute top-0 left-0 right-0 z-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-white flex items-center justify-center overflow-hidden shadow-lg">
              <img src={COMPANY_LOGO} alt={COMPANY_FULL} className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-display text-white text-lg font-bold tracking-tight leading-none" data-testid="brand">{COMPANY_NAME}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-gold font-bold mt-0.5">on {PRODUCT_NAME}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="#pricing" className="hidden sm:block text-white/80 hover:text-white text-sm font-semibold" data-testid="nav-pricing">Pricing</a>
            <button onClick={() => navigate("/intake")} className="hidden sm:block text-white/80 hover:text-white text-sm font-semibold" data-testid="nav-request-load">Request a load</button>
            <button onClick={() => navigate("/login")} className="text-white/80 hover:text-white text-sm font-semibold" data-testid="nav-signin">Sign in</button>
            <button onClick={() => navigate("/login")} className="btn-primary" data-testid="nav-get-started">Get started <ArrowRight size={16} /></button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[640px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 gradient-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 grid lg:grid-cols-12 gap-12 w-full">
          <div className="lg:col-span-7 text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-[11px] font-bold tracking-[0.2em] uppercase mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
              Freight Brokerage · Reimagined
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tighter leading-[0.95] mb-6">
              Move freight at <span className="text-gold">light speed.</span>
            </h1>
            <p className="text-lg text-slate-200 max-w-xl leading-relaxed mb-10">
              The operating system for modern freight brokers. Post loads, accept bids, track drivers, and bill — all in one place. Built for shippers, carriers, dispatchers, and drivers.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate("/login")} className="btn-primary text-base px-6 py-3" data-testid="hero-cta-start">
                Start moving <ArrowRight size={18} />
              </button>
              <button onClick={() => navigate("/login")} className="btn-outline text-base px-6 py-3 bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="hero-cta-demo">
                Try demo accounts
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-8 mt-12 text-sm">
              <div><div className="font-display text-3xl font-bold text-gold">12.4M</div><div className="text-slate-300">Loads moved</div></div>
              <div className="w-px h-10 bg-white/20" />
              <div><div className="font-display text-3xl font-bold text-gold">$2.8B</div><div className="text-slate-300">Freight value</div></div>
              <div className="w-px h-10 bg-white/20" />
              <div><div className="font-display text-3xl font-bold text-gold">98.6%</div><div className="text-slate-300">On-time</div></div>
            </div>
          </div>
          <div className="hidden lg:block lg:col-span-5">
            <div className="bg-white/95 backdrop-blur rounded-lg p-6 shadow-2xl border border-white/40">
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-bold mb-3">Active load #LD-4821</div>
              <div className="font-display text-2xl font-bold text-slate-900 mb-1">Chicago, IL → Dallas, TX</div>
              <div className="text-sm text-slate-500 mb-5">920 miles · Dry Van · 24,000 lbs</div>
              <div className="space-y-3">
                {[
                  { l: "Published", done: true },
                  { l: "3 bids received", done: true },
                  { l: "Bid accepted · $2,150", done: true },
                  { l: "Picked up · 09:32 CST", done: true },
                  { l: "In transit · Hub City, KS", done: false, active: true },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${s.done ? "bg-emerald-500" : s.active ? "bg-gold" : "bg-slate-200"}`}>
                      {s.done && <CheckCircle2 size={14} className="text-white" />}
                    </div>
                    <div className={`text-sm ${s.done ? "text-slate-700" : s.active ? "font-semibold text-slate-900" : "text-slate-400"}`}>{s.l}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-5 border-t border-slate-200 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Broker margin</div>
                  <div className="font-display text-xl font-bold text-navy">$304.20</div>
                </div>
                <span className="badge in_transit">In Transit</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="grid md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-5">
            <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-3">Built for logistics</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter text-navy">
              Every role.<br />One platform.
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-4">
            <p className="text-slate-600 text-lg leading-relaxed">
              SpeedyShip orchestrates the entire freight lifecycle — from intake to final POD — with role-based dashboards, intelligent bidding, weather-aware scheduling, and an audit-grade activity ledger.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            { i: Gavel, t: "Bidding engine", d: "Carriers bid; brokers accept with one click. Margin auto-computed per fee rule." },
            { i: CloudSun, t: "Weather alerts", d: "Pickup & dropoff forecasts with low/medium/high risk indicators per load." },
            { i: Truck, t: "Driver app", d: "Status updates, BOL & POD uploads, GPS-ready. Built for the road." },
            { i: FileText, t: "Documents", d: "PDF, JPG, PNG — secure uploads attached to every load. BOLs, PODs, rate confs." },
            { i: ShieldCheck, t: "Admin & fees", d: "Per-mile, flat, or percent fees. Role-based permissions and audit logs." },
            { i: CheckCircle2, t: "Stripe billing", d: "Monthly and annual subscriptions, invoices, payment recovery, and self-service account management." },
          ].map((f, i) => (
            <div key={i} className="card-base p-6 group hover:border-gold transition-colors">
              <div className="w-10 h-10 rounded-md bg-navy/5 flex items-center justify-center mb-4 group-hover:bg-gold/15 transition-colors">
                <f.i size={20} className="text-navy" strokeWidth={1.5} />
              </div>
              <div className="font-display text-xl font-bold text-navy mb-2">{f.t}</div>
              <div className="text-sm text-slate-600 leading-relaxed">{f.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-white border-t border-slate-100" data-testid="landing-pricing">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-3">Pricing</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter text-navy">Plans for every role.</h2>
            <p className="text-slate-600 text-lg mt-4">
              Start free with pay-as-you-go at <span className="font-bold text-navy">$0.99/load</span>, or pick a monthly plan. No contracts. Cancel anytime.
            </p>
          </div>

          {/* Audience toggle */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-slate-100 rounded-full p-1" data-testid="pricing-audience-toggle">
              {AUDIENCES.map((a) => (
                <button
                  key={a.key}
                  onClick={() => setAudience(a.key)}
                  className={`px-5 py-2 rounded-full text-xs font-bold tracking-wide uppercase transition-colors ${audience === a.key ? "bg-navy text-white" : "text-slate-500 hover:text-navy"}`}
                  data-testid={`audience-${a.key}`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
            {/* PAYG card always shown */}
            <div className="card-base p-7 flex flex-col relative" data-testid="landing-plan-payg">
              <div className="font-display text-2xl font-bold text-navy">Pay-as-you-go</div>
              <div className="text-[11px] font-semibold text-emerald-700 mt-1 min-h-[16px]">Zero monthly minimum</div>
              <div className="my-5 flex items-baseline gap-1">
                <span className="font-display text-5xl font-bold tracking-tighter text-navy">$0.99</span>
                <span className="text-slate-500 text-sm">/load</span>
              </div>
              <ul className="space-y-2.5 mb-7 flex-1">
                {["$0.99 per load post", "Credit packs of 5 / 25 / 50", "Full load board access", "Cancel anytime"].map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate("/login")} className="w-full btn-outline" data-testid="landing-plan-cta-payg">Get started</button>
            </div>

            {/* Audience-specific tiers */}
            {planKeys.map((key) => {
              const p = plans[key];
              return (
                <div
                  key={key}
                  className={`card-base p-7 flex flex-col relative ${p.popular ? "border-gold shadow-xl lg:scale-[1.03]" : ""}`}
                  data-testid={`landing-plan-${key}`}
                >
                  {p.popular && <div className="absolute -top-3 left-7 bg-gold text-navy text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded">Most popular</div>}
                  <div className="font-display text-2xl font-bold text-navy">{p.name}</div>
                  <div className="text-[11px] font-semibold text-emerald-700 mt-1 min-h-[16px]">{p.tagline}</div>
                  <div className="my-5 flex items-baseline gap-1">
                    <span className="font-display text-5xl font-bold tracking-tighter text-navy">${p.price}</span>
                    <span className="text-slate-500 text-sm">/mo</span>
                  </div>
                  <ul className="space-y-2.5 mb-7 flex-1">
                    {p.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => navigate("/login")}
                    className={`w-full ${p.popular ? "btn-primary" : "btn-outline"}`}
                    data-testid={`landing-plan-cta-${key}`}
                  >
                    {p.popular ? "Start free trial" : "Get started"}
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-center text-xs text-slate-500 mt-8">
            Driver, broker & shipper tiers (Gold · Silver · Platinum). Annual billing saves 2 months. Prices in USD.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-white py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tighter mb-6">
            Ready to <span className="text-gold">accelerate</span>?
          </h2>
          <p className="text-slate-300 text-lg mb-10 max-w-2xl mx-auto">
            Get started in 60 seconds. Sign in with Google and try every role — shipper, carrier, dispatcher, driver, or admin.
          </p>
          <button onClick={() => navigate("/login")} className="btn-primary text-base px-8 py-4" data-testid="cta-final">
            Open SpeedyShip <ArrowRight size={18} />
          </button>
        </div>
      </section>

      <footer className="py-10 border-t border-slate-200 text-center text-sm text-slate-500">
        © 2026 SpeedyShip · {COMPANY_FULL} · Built with logistics in mind.
        <div className="text-xs text-slate-400 mt-1">{COMPANY_FULL} is owned by {PARENT_COMPANY}.</div>
      </footer>
    </div>
  );
}
